import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPayout, deletePayout, listPayouts, payoutKeys } from '@/lib/api/payouts';
import {
  endShift,
  getMyShift,
  getSalesByShift,
  getShiftAlerts,
  listShiftSessions,
  shiftKeys,
  startShift,
  type ShiftRange,
  type ShiftSessionListParams,
} from '@/lib/api/shifts';

const MINUTE = 60_000;

/** The signed-in cashier's shift status. Polled every minute so a day rollover / 23:00 is noticed. */
export function useMyShift(enabled: boolean) {
  return useQuery({
    queryKey: shiftKeys.me,
    queryFn: getMyShift,
    enabled,
    refetchInterval: MINUTE,
    refetchOnWindowFocus: true,
  });
}

export function useShiftAlerts(enabled: boolean) {
  return useQuery({
    queryKey: shiftKeys.alerts,
    queryFn: getShiftAlerts,
    enabled,
    refetchInterval: MINUTE,
    refetchOnWindowFocus: true,
  });
}

export function useShiftSessions(params: ShiftSessionListParams, enabled = true) {
  return useQuery({
    queryKey: shiftKeys.sessions(params),
    queryFn: () => listShiftSessions(params),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useSalesByShift(range: ShiftRange, enabled = true) {
  return useQuery({
    queryKey: shiftKeys.salesByShift(range),
    queryFn: () => getSalesByShift(range),
    enabled,
  });
}

export function useStartShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: startShift,
    onSettled: () => qc.invalidateQueries({ queryKey: shiftKeys.all }),
  });
}

export function useEndShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: endShift,
    onSettled: () => qc.invalidateQueries({ queryKey: shiftKeys.all }),
  });
}

export function usePayoutsToday(enabled: boolean) {
  return useQuery({
    queryKey: payoutKeys.list({ today: true }),
    queryFn: () => listPayouts(),
    enabled,
  });
}

export function useCreatePayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createPayout,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: payoutKeys.all });
      void qc.invalidateQueries({ queryKey: shiftKeys.all });
    },
  });
}

export function useDeletePayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deletePayout,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: payoutKeys.all });
      void qc.invalidateQueries({ queryKey: shiftKeys.all });
    },
  });
}
